/**
 * Deep Linking Handler for Capacitor
 * Handles app links like mellow://chat/123 or https://mellow.app/chat/123
 */

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { logger } from './logger';

let deepLinkInitialized = false;

/**
 * Parse a deep link URL and extract route info
 * @param {string} url - The deep link URL
 * @returns {Object} Parsed route info { path, params }
 */
const parseDeepLink = (url) => {
  try {
    // Handle custom scheme (mellow://path) and universal links (https://mellow.app/path)
    let path = '';

    if (url.startsWith('mellow://')) {
      path = url.replace('mellow://', '/');
    } else if (url.includes('mellow.app')) {
      const urlObj = new URL(url);
      path = urlObj.pathname + urlObj.search;
    } else {
      // Generic URL handling
      const urlObj = new URL(url);
      path = urlObj.pathname + urlObj.search;
    }

    // Extract path segments
    const segments = path.split('/').filter(Boolean);

    return {
      path,
      segments,
      fullUrl: url
    };
  } catch (error) {
    logger.error('Error parsing deep link', { url, error });
    return null;
  }
};

/**
 * Handle incoming deep link
 * @param {string} url - The deep link URL
 */
const handleDeepLink = (url) => {
  const parsed = parseDeepLink(url);

  if (!parsed) {
    logger.warn('Could not parse deep link', { url });
    return;
  }

  logger.info('Handling deep link', { path: parsed.path });

  const [firstSegment, secondSegment] = parsed.segments;

  switch (firstSegment) {
    case 'chat':
      // Deep link to specific chat: mellow://chat/{chatId}
      if (secondSegment) {
        window.location.href = `/chat/${secondSegment}`;
      }
      break;

    case 'profile':
      // Deep link to profile
      window.location.href = '/profile';
      break;

    case 'discover':
    case 'discovery':
      // Deep link to discovery
      window.location.href = '/';
      break;

    case 'settings':
      // Deep link to settings
      window.location.href = '/settings';
      break;

    case 'share':
      // Handle shared content: mellow://share?text=...
      if (secondSegment) {
        // Could be a shared chat or content
        window.location.href = `/shared/${secondSegment}`;
      }
      break;

    default:
      // Unknown route, go to home
      logger.warn('Unknown deep link route', { segment: firstSegment });
      window.location.href = '/';
      break;
  }
};

/**
 * Initialize deep linking handler
 * Call this early in app initialization
 */
export const initDeepLinks = async () => {
  if (!Capacitor.isNativePlatform()) {
    logger.info('Deep links not needed on web platform');
    return;
  }

  if (deepLinkInitialized) {
    logger.info('Deep links already initialized');
    return;
  }

  try {
    // Handle app opened via deep link (cold start)
    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url) {
      logger.info('App launched via deep link', { url: launchUrl.url });
      // Delay handling to allow app to fully initialize
      setTimeout(() => handleDeepLink(launchUrl.url), 100);
    }

    // Handle deep links when app is already running (warm start)
    App.addListener('appUrlOpen', (event) => {
      logger.info('App URL opened', { url: event.url });
      handleDeepLink(event.url);
    });

    deepLinkInitialized = true;
    logger.info('Deep links initialized');
  } catch (error) {
    logger.error('Error initializing deep links', error);
  }
};

/**
 * Generate a shareable deep link
 * @param {string} type - Type of content (chat, profile, etc.)
 * @param {string} id - Content ID
 * @returns {string} Shareable URL
 */
export const generateShareLink = (type, id) => {
  // Use universal link format for better compatibility
  const baseUrl = 'https://mellow.app';

  switch (type) {
    case 'chat':
      return `${baseUrl}/chat/${id}`;
    case 'profile':
      return `${baseUrl}/profile/${id}`;
    default:
      return baseUrl;
  }
};
